import React, { useState, useEffect, useRef } from 'react';

// GUARANTEED notification component that bypasses all toast blocking mechanisms
const DirectNotification = ({ bookId, bookProcessingStatuses }) => {
  const [visible, setVisible] = useState(false);
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('');
  const [notificationCount, setNotificationCount] = useState(0);
  const lastStatusRef = useRef(null);
  const timerRef = useRef(null);
  
  // Force the notification to show for these statuses no matter what
  const FORCE_NOTIFICATION_STATUSES = [
    'generatingCharacter',
    'generatingCoverImage', 
    'updatingImageElements'
  ];

  useEffect(() => {
    // Force a notification check every 1 second regardless of dependency changes
    const checkInterval = setInterval(() => {
      if (bookId && bookProcessingStatuses && bookProcessingStatuses[bookId]) {
        const currentStatus = bookProcessingStatuses[bookId].status;
        
        // If status is one we want to notify about and it hasn't changed since last time
        if (FORCE_NOTIFICATION_STATUSES.includes(currentStatus)) {
          // Only show notification if status has changed or hasn't been shown in 5 seconds
          if (currentStatus !== lastStatusRef.current || (Date.now() - (timerRef.current || 0) > 5000)) {
            setStatus(currentStatus);
            
            // Create status-specific messages
            let statusMessage = "";
            if (currentStatus === 'generatingCoverImage') {
              statusMessage = "Creating your book cover image...";
            } else if (currentStatus === 'generatingCharacter') {
              statusMessage = "Generating character images...";
            } else if (currentStatus === 'updatingImageElements') {
              statusMessage = "Adding images to your book...";
            }
            
            setMessage(`⚡ Design is ready! ${statusMessage}`);
            setVisible(true);
            setNotificationCount(prev => prev + 1);
            console.log(`🔔🔔🔔 GUARANTEED NOTIFICATION #${notificationCount+1} SHOWN: ${currentStatus}`);
            
            // Track when we last showed this status
            lastStatusRef.current = currentStatus;
            timerRef.current = Date.now();
          }
        } else if (currentStatus === 'completed') {
          setVisible(false);
        }
      }
    }, 1000);
    
    return () => clearInterval(checkInterval);
  }, [bookId, bookProcessingStatuses, notificationCount]);

  if (!visible) return null;
  
  // Add animation styles when component mounts
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes fadeInRight {
        from { opacity: 0; transform: translateX(50px); }
        to { opacity: 1; transform: translateX(0); }
      }
    `;
    document.head.appendChild(style);
    
    // Cleanup when unmounted
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  
  // Completely bypass the toast system with our own notification
  return (
    <div 
      style={{
        position: 'fixed',
        top: '80px', // Position higher to make it more visible
        right: '20px',
        backgroundColor: '#ff5722', // Use a bright orange color for visibility
        color: 'white',
        padding: '18px',
        borderRadius: '8px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.3), 0 0 0 2px rgba(255,255,255,0.2)',
        zIndex: 9999999, // Extra high z-index to ensure it's on top
        maxWidth: '350px',
        animation: 'fadeInRight 0.5s',
        border: '2px solid white'
      }}
    >
      <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '8px' }}>
        ⚡ DESIGN IS READY!
      </div>
      <div style={{ fontSize: '14px' }}>{message}</div>
      <div style={{ fontSize: '12px', marginTop: '8px', opacity: 0.8 }}>
        Status: {status} • Notification #{notificationCount}
      </div>
      <button 
        style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          background: 'none',
          border: 'none',
          color: 'white',
          cursor: 'pointer',
          fontSize: '18px',
          fontWeight: 'bold'
        }}
        onClick={() => setVisible(false)}
      >
        ×
      </button>
    </div>
  );
};

export default DirectNotification;
